class BtlSortPicker extends HTMLElement {
  static enhance(root) {
    root.querySelectorAll('.facet-filters__sort').forEach((select) => {
      if (select.closest('btl-sort-picker')) return;
      const picker = document.createElement('btl-sort-picker');
      const parent = select.parentNode;
      picker.appendChild(select);
      parent.prepend(picker);
    });
  }
  connectedCallback() {
    if (this.select) return;
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.sortLabel = this.select.labels?.[0]?.textContent.trim() || 'Sort by';
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'btl-sort__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.valueLabel = document.createElement('span');
    this.trigger.appendChild(this.valueLabel);
    const caret = this.parentElement.querySelector(':scope > .svg-wrapper')?.cloneNode(true);
    if (caret) this.trigger.appendChild(caret);
    this.panel = document.createElement('div');
    this.panel.className = 'btl-sort__panel facets__display';
    this.panel.id = this.select.id + '-options';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'listbox');
    this.panel.setAttribute('aria-label', this.sortLabel);
    this.trigger.setAttribute('aria-controls', this.panel.id);
    this.buttons = Array.from(this.select.options, (option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btl-sort__option';
      button.setAttribute('role', 'option');
      button.tabIndex = -1;
      button.value = option.value;
      button.textContent = option.label;
      button.disabled = option.disabled;
      this.panel.appendChild(button);
      return button;
    });
    this.append(this.trigger, this.panel);
    this.onClick = (event) => {
      const option = event.target.closest('.btl-sort__option');
      if (option && this.contains(option)) {
        const changed = this.select.value !== option.value;
        this.select.value = option.value;
        this.sync();
        this.close(true);
        if (changed) {
          const form = this.select.form;
          const inputTarget = form && !form.contains(this.select) ? form : this.select;
          inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (this.trigger.contains(event.target)) {
        this.panel.hidden ? this.open() : this.close(false);
      }
    };
    this.onKeyDown = (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        return;
      }
      if (event.key === 'Tab' && !this.panel.hidden) {
        this.close(true);
        return;
      }
      if (this.trigger.contains(event.target) && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.open();
        return;
      }
      if (this.panel.hidden || !this.panel.contains(event.target)) return;
      const buttons = this.buttons.filter((button) => !button.disabled);
      const index = buttons.indexOf(document.activeElement);
      let next;
      if (event.key === 'ArrowDown') next = buttons[(index + 1) % buttons.length];
      if (event.key === 'ArrowUp') next = buttons[(index - 1 + buttons.length) % buttons.length];
      if (event.key === 'Home') next = buttons[0];
      if (event.key === 'End') next = buttons[buttons.length - 1];
      if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        this.searchText = Date.now() - (this.lastKeyTime || 0) < 700 ? (this.searchText || '') + event.key : event.key;
        this.lastKeyTime = Date.now();
        next = buttons.find((button) => button.textContent.toLocaleLowerCase().startsWith(this.searchText.toLocaleLowerCase()));
      }
      if (next) {
        event.preventDefault();
        next.focus({ preventScroll: true });
      }
    };
    this.onOutside = (event) => { if (!this.contains(event.target)) this.close(false); };
    this.onChange = () => this.sync();
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.select.addEventListener('change', this.onChange);
    document.addEventListener('pointerdown', this.onOutside);
    this.sync();
    this.setAttribute('data-ready', '');
  }
  sync() {
    const selectedLabel = this.select.selectedOptions[0]?.label || "";
    this.valueLabel.textContent = this.select.dataset.sortCaption || selectedLabel;
    this.trigger.setAttribute("aria-label", this.sortLabel + " " + selectedLabel);
    this.buttons.forEach((button) => button.setAttribute('aria-selected', String(button.value === this.select.value)));
  }
  open() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.closest("btl-mobile-facets") && matchMedia("(max-width: 749px)").matches && !document.body.classList.contains("overflow-hidden-mobile")) {
      document.body.classList.add("overflow-hidden-mobile");
      this.ownsMobileScrollLock = true;
    }
    (this.buttons.find((button) => button.value === this.select.value) || this.buttons[0])?.focus({ preventScroll: true });
  }
  close(restoreFocus) {
    if (this.panel) this.panel.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.ownsMobileScrollLock) {
      document.body.classList.remove("overflow-hidden-mobile");
      this.ownsMobileScrollLock = false;
    }
    if (restoreFocus) this.trigger?.focus({ preventScroll: true });
  }
  disconnectedCallback() {
    this.close(false);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.select?.removeEventListener('change', this.onChange);
    document.removeEventListener('pointerdown', this.onOutside);
    this.trigger?.remove();
    this.panel?.remove();
    this.removeAttribute("data-ready");
    this.select = null;
  }
}
customElements.define('btl-sort-picker', BtlSortPicker);

class BtlMobileFacets extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const form = this.form = document.getElementById(this.dataset.mobileForm);
    this.filterButton = this.querySelector("[data-mobile-filter-open]");
    this.filterLabel = this.filterButton?.textContent.trim() || "Filter";
    const heading = form?.querySelector(".mobile-facets__heading");
    if (heading) heading.textContent = this.filterLabel;
    if (this.filterButton) {
      this.badge = document.createElement("span");
      this.badge.className = "btl-mobile-facets__count";
      this.badge.setAttribute("aria-hidden", "true");
      this.badge.hidden = true;
      this.filterButton.querySelector(".svg-wrapper")?.append(this.badge);
    }
    const select = form?.querySelector("#SortBy-mobile");
    const slot = this.querySelector("[data-mobile-sort-slot]");
    if (select && slot) {
      this.select = select;
      this.sortWrapper = select.closest(".select");
      this.placeholder = document.createComment("Mobile sort position");
      this.sortWrapper.before(this.placeholder);
      select.setAttribute("form", form.id);
      select.classList.add("facet-filters__sort");
      select.dataset.sortCaption = "Sort";
      slot.append(this.sortWrapper);
      BtlSortPicker.enhance(slot);
    }
    this.drawerDetails = form?.closest("menu-drawer")?.querySelector("details");
    this.drawerSummary = this.drawerDetails?.querySelector(":scope > summary");
    this.filterButton?.addEventListener("click", () => {
      if (!this.drawerDetails?.open) this.drawerSummary?.click();
    }, { signal });
    form?.addEventListener("input", event => {
      const choice = event.target.closest("[data-mobile-price-choice]");
      if (!choice || !matchMedia("(max-width: 749px)").matches) return;
      event.stopImmediatePropagation();
      const group = choice.closest("[data-mobile-price-ranges]");
      const price = group.parentElement.querySelector("price-range");
      const minimum = price?.querySelector('input[name$=".gte"]');
      const maximum = price?.querySelector('input[name$=".lte"]');
      if (!minimum || !maximum) return;
      group.querySelectorAll("input").forEach(input => { if (input !== choice) input.checked = false; });
      minimum.value = choice.checked ? choice.dataset.min : "";
      maximum.value = choice.checked ? choice.dataset.max : "";
      this.sync();
      minimum.dispatchEvent(new Event("input", { bubbles: true }));
    }, { capture: true, signal });
    form?.addEventListener("click", event => {
      if (!matchMedia("(max-width: 749px)").matches) return;
      const summary = event.target.closest(".mobile-facets__details > summary");
      if (!summary || !form.contains(summary)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.activateFilter(summary.parentElement);
    }, { capture: true, signal });
    form?.addEventListener("keydown", event => {
      if (event.key !== "Escape" || !matchMedia("(max-width: 749px)").matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.drawerSummary?.click();
    }, { capture: true, signal });
    this.drawerSummary?.setAttribute("aria-label", "Close filters");
    form?.setAttribute("role", "dialog");
    form?.setAttribute("aria-modal", "true");
    form?.setAttribute("aria-label", this.filterLabel);
    this.filterButton?.setAttribute("aria-controls", form?.id || "");
    this.filterButton?.setAttribute("aria-haspopup", "dialog");
    this.drawerDetails?.addEventListener("keydown", event => {
      if (!this.drawerDetails.open || !matchMedia("(max-width: 749px)").matches) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.drawerSummary?.click();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(this.drawerDetails.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex="0"]')).filter(control => !control.disabled && control.getClientRects().length && getComputedStyle(control).visibility !== "hidden");
      if (!controls.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const index = controls.indexOf(document.activeElement);
      const next = (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
      controls[next].focus({ preventScroll: true });
    }, { capture: true, signal });
    this.drawerDetails?.addEventListener("toggle", () => {
      this.filterButton?.setAttribute("aria-expanded", String(this.drawerDetails.open));
      if (this.drawerDetails.open) {
        this.sync();
        if (matchMedia("(max-width: 749px)").matches) form.querySelector("details.mobile-facets__details[open] > summary")?.focus({ preventScroll: true });
      } else if (matchMedia("(max-width: 749px)").matches) this.filterButton?.focus({ preventScroll: true });
    }, { signal });
    const header = document.querySelector("btl-luxury-header");
    this.aside = this.closest(".facets-wrapper");
    if (header && this.aside) {
      this.originalAsideMinHeight = this.aside.style.minHeight;
      const update = () => {
        const floating = matchMedia("(max-width: 749px)").matches && header.hasAttribute("data-sticky-hidden");
        if (floating) {
          const rect = this.aside.getBoundingClientRect();
          this.aside.style.setProperty("--btl-mobile-filter-left", rect.left + "px");
          this.aside.style.setProperty("--btl-mobile-filter-width", rect.width + "px");
          this.aside.style.minHeight = this.getBoundingClientRect().height + "px";
        } else this.aside.style.minHeight = this.originalAsideMinHeight;
        this.aside.toggleAttribute("data-mobile-floating", floating);
        this.aside.style.setProperty("--btl-mobile-filter-offset", (floating ? 0 : header.getBoundingClientRect().height) + "px");
      };
      this.headerResize = new ResizeObserver(update);
      this.headerResize.observe(header);
      this.headerResize.observe(this);
      this.headerState = new MutationObserver(update);
      this.headerState.observe(header, { attributes: true, attributeFilter: ["data-sticky-hidden"] });
      window.addEventListener("resize", update, { signal });
      update();
    }
    this.sync();
  }
  activateFilter(active) {
    this.form?.querySelectorAll("details.mobile-facets__details").forEach(details => {
      details.open = details === active;
      details.querySelector(":scope > summary")?.setAttribute("aria-expanded", String(details.open));
    });
  }
  sync() {
    if (!this.form) return;
    this.form.querySelectorAll("[data-mobile-price-ranges]").forEach(group => {
      const price = group.parentElement.querySelector("price-range");
      const minimum = price?.querySelector('input[name$=".gte"]')?.value || "";
      const maximum = price?.querySelector('input[name$=".lte"]')?.value || "";
      group.querySelectorAll("[data-mobile-price-choice]").forEach(input => {
        const matches = (value, bound) => value === "" || bound === "" ? value === bound : Number(value) === Number(bound);
        input.checked = (minimum !== "" || maximum !== "") && matches(minimum, input.dataset.min) && matches(maximum, input.dataset.max);
      });
    });
    const checked = this.form.querySelectorAll('input[type="checkbox"][name^="filter."]:checked').length;
    const prices = Array.from(this.form.querySelectorAll('input[name^="filter."][name$=".gte"], input[name^="filter."][name$=".lte"]'));
    const count = checked + Number(prices.some(input => input.value.trim() !== ""));
    if (this.badge) {
      this.badge.textContent = String(count);
      this.badge.hidden = count === 0;
    }
    this.filterButton?.setAttribute("aria-label", this.filterLabel + (count ? ", " + count + " active filters" : ""));
    if (this.drawerDetails?.open && matchMedia("(max-width: 749px)").matches) {
      const active = this.form.querySelector("details.mobile-facets__details[open]") || this.form.querySelector("details.mobile-facets__details");
      if (active) this.activateFilter(active);
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.headerResize?.disconnect();
    this.headerState?.disconnect();
    this.aside?.style.removeProperty("--btl-mobile-filter-offset");
    this.aside?.style.removeProperty("--btl-mobile-filter-left");
    this.aside?.style.removeProperty("--btl-mobile-filter-width");
    this.aside?.removeAttribute("data-mobile-floating");
    if (this.aside) this.aside.style.minHeight = this.originalAsideMinHeight || "";
    this.badge?.remove();
    if (this.placeholder?.isConnected && this.sortWrapper) {
      this.placeholder.replaceWith(this.sortWrapper);
      this.select.removeAttribute("form");
    }
  }
}
customElements.define("btl-mobile-facets", BtlMobileFacets);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    BtlSortPicker.enhance(this);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const mobilePicker = document.querySelector("btl-mobile-facets btl-sort-picker");
    const incomingMobileSort = parsedHTML.querySelector("#SortBy-mobile");
    if (mobilePicker?.select && incomingMobileSort) {
      mobilePicker.select.value = incomingMobileSort.value;
      mobilePicker.sync();
      incomingMobileSort.closest(".js-filter")?.remove();
    }
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
    BtlSortPicker.enhance(document);
    document.querySelectorAll("btl-mobile-facets").forEach(bar => bar.sync());

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
